import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useChurch } from "@/hooks/useChurch";
import { usePermissions } from "@/hooks/usePermissions";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Download, Loader2, Share2, Users, TrendingUp, CalendarRange, FolderKanban, ChevronRight, ArrowUpRight, ArrowDownRight } from "lucide-react";
import ContributionStatements from "./ContributionStatements";
import { toast } from "sonner";
import jsPDF from "jspdf";
import { sortGeneralFirstByName } from "@/lib/utils";
import { cn } from "@/lib/utils";

// ── helpers ──────────────────────────────────────────────────────────────────
const currentYear = new Date().getFullYear();
const YEARS = Array.from({ length: 5 }, (_, i) => String(currentYear - i));
const MONTHS_LABEL = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const MONTHS_SHORT = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"] as const;
const MINISTRY_IDS_NOTE_PREFIX = "Ministry IDs: ";
const LEGACY_ORG_IDS_NOTE_PREFIX = "Organization IDs: ";

function fmt(v: number) {
  return "$" + v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function fmtShort(v: number) {
  return "$" + v.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
}
function fmtDate(d: string) {
  return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function getOrgIdsFromNotes(notes: string | null): string[] {
  if (!notes) return [];
  const lines = notes.split("\n").map((line) => line.trim());
  const idsLine = lines.find((line) =>
    line.startsWith(MINISTRY_IDS_NOTE_PREFIX) || line.startsWith(LEGACY_ORG_IDS_NOTE_PREFIX)
  );
  if (!idsLine) return [];
  return idsLine
    .replace(MINISTRY_IDS_NOTE_PREFIX, "")
    .replace(LEGACY_ORG_IDS_NOTE_PREFIX, "")
    .split(">")
    .map((id) => id.trim())
    .filter(Boolean);
}

async function loadImageAsBase64(url: string): Promise<{ data: string; width: number; height: number } | null> {
  return new Promise((resolve) => {
    if (!url) { resolve(null); return; }
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      try {
        const canvas = document.createElement("canvas");
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext("2d");
        ctx?.drawImage(img, 0, 0);
        resolve({ data: canvas.toDataURL("image/png"), width: img.naturalWidth, height: img.naturalHeight });
      } catch { resolve(null); }
    };
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

interface ChurchInfo {
  church_name: string;
  logo_url: string | null;
  address: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  zip_code: string | null;
  phone: string | null;
  email: string | null;
}

function getChurchInfoLines(church: ChurchInfo) {
  const lines: string[] = [];
  const addressParts = [church.address, [church.city, church.state, church.zip_code].filter(Boolean).join(", "), church.country].filter(Boolean);
  if (addressParts.length > 0) lines.push(addressParts.join(", "));
  if (church.phone) lines.push(`Phone: ${church.phone}`);
  if (church.email) lines.push(`Email: ${church.email}`);
  return lines;
}

function ChurchReportHeader({ profile }: { profile: ChurchInfo | null }) {
  if (!profile) return null;
  const infoLines = getChurchInfoLines(profile);
  return (
    <div className="flex items-start gap-4 pb-4 border-b border-border">
      {profile.logo_url && (
        <img src={profile.logo_url} alt="Church logo" className="w-14 h-14 rounded-lg object-cover shrink-0" />
      )}
      <div>
        <h2 className="text-lg font-bold text-foreground">{profile.church_name}</h2>
        {infoLines.map((line, i) => (
          <p key={i} className="text-xs text-muted-foreground">{line}</p>
        ))}
      </div>
    </div>
  );
}

async function shareOrMailto(doc: jsPDF, fileName: string, title: string, bodyText: string) {
  const blob = doc.output("blob");
  if (navigator.share && navigator.canShare) {
    const file = new File([blob], fileName, { type: "application/pdf" });
    if (navigator.canShare({ files: [file] })) {
      await navigator.share({ title, files: [file] });
      return;
    }
  }
  const subject = encodeURIComponent(title);
  const body = encodeURIComponent(bodyText);
  window.open(`mailto:?subject=${subject}&body=${body}`, "_self");
}

type ReportType = "contributions" | "income_statement" | "budget_summary" | "project_reports";

interface ReportCard {
  id: ReportType;
  label: string;
  description: string;
  icon: React.ElementType;
}

const REPORT_CARDS: ReportCard[] = [
  { id: "contributions", label: "Contributor Statements", description: "Individual giving statements per member, downloadable as PDF.", icon: Users },
  { id: "income_statement", label: "Income Statement", description: "Summary of all income and expenses grouped by category.", icon: TrendingUp },
  { id: "budget_summary", label: "Budget Report", description: "Annual budget vs. actual spending with variance analysis.", icon: CalendarRange },
  { id: "project_reports", label: "Project Reports", description: "Per-project budget vs. actuals and completion status.", icon: FolderKanban },
];

const REPORT_PREVIEW_DIALOG_CLASS = "w-[calc(100vw-2rem)] max-w-3xl min-h-[75vh] max-h-[90vh] overflow-y-auto";

// ── Income Statement ──────────────────────────────────────────────────────────
function IncomeStatement({ formatCurrency }: { formatCurrency: (v: number) => string }) {
  const { profile } = useChurch();
  const { getFinanceOrganizationIds, hasFinanceScopedAccess, isChurchAdmin } = usePermissions();
  const financeScoped = !isChurchAdmin && hasFinanceScopedAccess();
  const allowedOrgIds = getFinanceOrganizationIds();
  const [year, setYear] = useState(String(currentYear));
  const [filterMinistry, setFilterMinistry] = useState("all");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations", "income_statement_scope", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name")
        .eq("church_id", profile!.id)
        .order("name");
      if (error) throw error;
      return sortGeneralFirstByName((data || []) as { id: string; name: string }[]);
    },
    enabled: !!profile?.id,
  });

  const sortedOrganizations = useMemo(() => {
    const sorted = sortGeneralFirstByName(organizations);
    if (!financeScoped) return sorted;
    return sorted.filter((o) => allowedOrgIds.includes(o.id));
  }, [organizations, financeScoped, allowedOrgIds]);

  const { data: records = [], isLoading } = useQuery({
    queryKey: ["finance_records_report", profile?.id, year],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("finance_records")
        .select("amount, category, record_type, date, notes")
        .eq("church_id", profile!.id)
        .gte("date", `${year}-01-01`)
        .lte("date", `${year}-12-31`);
      if (error) throw error;
      return data as { amount: number; category: string; record_type: string; date: string; notes: string | null }[];
    },
    enabled: !!profile?.id,
  });

  const scopedRecords = useMemo(() => {
    if (!financeScoped) return records;
    return records.filter((r) => {
      const ids = getOrgIdsFromNotes(r.notes);
      return ids.some((id) => allowedOrgIds.includes(id));
    });
  }, [records, financeScoped, allowedOrgIds]);

  const filteredRecords = useMemo(() => {
    if (filterMinistry === "all") return scopedRecords;
    if (filterMinistry === "__none__") {
      return scopedRecords.filter((r) => getOrgIdsFromNotes(r.notes).length === 0);
    }
    return scopedRecords.filter((r) => getOrgIdsFromNotes(r.notes).includes(filterMinistry));
  }, [scopedRecords, filterMinistry]);

  const selectedMinistryName = useMemo(() => {
    if (filterMinistry === "all") return "All Ministries";
    if (filterMinistry === "__none__") return "No Ministry";
    return organizations.find((o) => o.id === filterMinistry)?.name || "Unknown Ministry";
  }, [filterMinistry, organizations]);

  const { incomeByCategory, expenseByCategory, totalIncome, totalExpenses } = useMemo(() => {
    const incomeMap: Record<string, number> = {};
    const expenseMap: Record<string, number> = {};
    filteredRecords.forEach((r) => {
      const amt = Number(r.amount);
      if (r.record_type === "income") incomeMap[r.category] = (incomeMap[r.category] || 0) + amt;
      else expenseMap[r.category] = (expenseMap[r.category] || 0) + amt;
    });
    const totalIncome = Object.values(incomeMap).reduce((a, b) => a + b, 0);
    const totalExpenses = Object.values(expenseMap).reduce((a, b) => a + b, 0);
    return {
      incomeByCategory: Object.entries(incomeMap).sort((a, b) => b[1] - a[1]),
      expenseByCategory: Object.entries(expenseMap).sort((a, b) => b[1] - a[1]),
      totalIncome,
      totalExpenses,
    };
  }, [filteredRecords]);

  const netIncome = totalIncome - totalExpenses;
  const positiveTextClass = "text-emerald-700";
  const negativeTextClass = "text-red-700";
  const positiveBgClass = "bg-emerald-50";
  const negativeBgClass = "bg-red-50";
  const positiveBorderClass = "border-emerald-300/60";
  const negativeBorderClass = "border-red-300/60";

  const buildPDF = async () => {
    if (!profile) return null;
    setDownloading(true);
    try {
      const doc = new jsPDF({ unit: "mm", format: "a4" });
      const pageW = doc.internal.pageSize.getWidth();
      const mL = 20, mR = 20;
      const contentW = pageW - mL - mR;
      let y = 20;

      let logoHeight = 0;
      if (profile.logo_url) {
        const logoInfo = await loadImageAsBase64(profile.logo_url);
        if (logoInfo) {
          const maxSize = 18;
          const aspectRatio = logoInfo.width / logoInfo.height;
          let logoWidth = maxSize;
          let logoH = maxSize;
          if (aspectRatio > 1) { logoH = maxSize / aspectRatio; }
          else { logoWidth = maxSize * aspectRatio; }
          try { doc.addImage(logoInfo.data, "PNG", mL, y, logoWidth, logoH); } catch {}
          logoHeight = logoH;
        }
      }
      const headerX = logoHeight > 0 ? mL + 22 : mL;
      doc.setFontSize(16); doc.setFont("helvetica", "bold"); doc.setTextColor(0);
      doc.text(profile.church_name, headerX, y + 7);
      const infoLines = getChurchInfoLines(profile);
      doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.setTextColor(100);
      infoLines.forEach((line, i) => doc.text(line, headerX, y + 12 + i * 4));
      y += Math.max(22, 12 + infoLines.length * 4 + 4);
      doc.setDrawColor(200); doc.setLineWidth(0.5); doc.line(mL, y, pageW - mR, y); y += 8;

      doc.setFontSize(14); doc.setFont("helvetica", "bold"); doc.setTextColor(0);
      doc.text("INCOME STATEMENT", mL, y); y += 7;
      doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(80);
      doc.text(`Year: ${year}`, mL, y); y += 5;
      doc.text(`Ministry: ${selectedMinistryName}`, mL, y); y += 5;
      doc.text(`Generated: ${new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`, mL, y); y += 10;
      doc.setDrawColor(220); doc.line(mL, y, pageW - mR, y); y += 8;

      doc.setFontSize(11); doc.setFont("helvetica", "bold"); doc.setTextColor(0);
      doc.text("INCOME", mL, y); y += 7;
      const colAmt = pageW - mR - 16;
      const colPct = pageW - mR;

      // ── INCOME section ──
      doc.setFillColor(240, 240, 240); doc.rect(mL, y - 4, contentW, 7, "F");
      doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(80);
      doc.text("Category", mL + 2, y); doc.text("Amount", colAmt, y, { align: "right" }); doc.text("%", colPct, y, { align: "right" }); y += 8;
      doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(30);
      incomeByCategory.forEach(([cat, amt]) => {
        if (y > 270) { doc.addPage(); y = 20; }
        const pct = totalIncome > 0 ? Math.round((amt / totalIncome) * 100) : 0;
        doc.setTextColor(30); doc.text(cat, mL + 4, y);
        doc.text(fmt(amt), colAmt, y, { align: "right" });
        doc.setTextColor(100); doc.text(`${pct}%`, colPct, y, { align: "right" }); y += 6;
      });
      y += 2; doc.setFillColor(240, 250, 240); doc.rect(mL, y - 4, contentW, 7, "F");
      doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(21, 128, 61);
      doc.text("Total Income", mL + 2, y); doc.text(fmt(totalIncome), colAmt, y, { align: "right" });
      doc.setTextColor(80); doc.text("100%", colPct, y, { align: "right" }); y += 12;

      doc.setFontSize(11); doc.setFont("helvetica", "bold"); doc.setTextColor(0);
      doc.text("EXPENSES", mL, y); y += 7;
      // ── EXPENSES section ──
      doc.setFillColor(240, 240, 240); doc.rect(mL, y - 4, contentW, 7, "F");
      doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(80);
      doc.text("Category", mL + 2, y); doc.text("Amount", colAmt, y, { align: "right" }); doc.text("%", colPct, y, { align: "right" }); y += 8;
      doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(30);
      expenseByCategory.forEach(([cat, amt]) => {
        if (y > 270) { doc.addPage(); y = 20; }
        const pct = totalExpenses > 0 ? Math.round((amt / totalExpenses) * 100) : 0;
        doc.setTextColor(30); doc.text(cat, mL + 4, y);
        doc.text(fmt(amt), colAmt, y, { align: "right" });
        doc.setTextColor(100); doc.text(`${pct}%`, colPct, y, { align: "right" }); y += 6;
      });
      y += 2; doc.setFillColor(255, 240, 240); doc.rect(mL, y - 4, contentW, 7, "F");
      doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(185, 28, 28);
      doc.text("Total Expenses", mL + 2, y); doc.text(fmt(totalExpenses), colAmt, y, { align: "right" });
      doc.setTextColor(80); doc.text("100%", colPct, y, { align: "right" }); y += 12;

      doc.setDrawColor(50); doc.setLineWidth(0.8); doc.line(mL, y, pageW - mR, y); y += 8;
      doc.setFontSize(12); doc.setFont("helvetica", "bold"); doc.setTextColor(0);
      doc.text("NET INCOME", mL, y);
      if (netIncome >= 0) doc.setTextColor(21, 128, 61); else doc.setTextColor(185, 28, 28);
      doc.text((netIncome >= 0 ? "+" : "") + fmt(netIncome), colAmt, y, { align: "right" }); y += 12;
      doc.setFont("helvetica", "italic"); doc.setFontSize(8); doc.setTextColor(120);
      doc.text(`This report is generated from recorded financial transactions for ${year}.`, mL, y, { maxWidth: contentW });
      return doc;
    } finally { setDownloading(false); }
  };

  const [sharing, setSharing] = useState(false);

  const handleShare = async () => {
    if (!profile) return;
    setSharing(true);
    try {
      const doc = await buildPDF();
      if (!doc) return;
      const lines = [
        `Income Statement — ${year}`,
        `Ministry: ${selectedMinistryName}`,
        "",
        "INCOME",
        ...incomeByCategory.map(([cat, amt]) => `  ${cat}: ${fmt(amt)}`),
        `Total Income: ${fmt(totalIncome)}`,
        "",
        "EXPENSES",
        ...expenseByCategory.map(([cat, amt]) => `  ${cat}: ${fmt(amt)}`),
        `Total Expenses: ${fmt(totalExpenses)}`,
        "",
        `Net Income: ${netIncome >= 0 ? "+" : ""}${fmt(netIncome)}`,
      ];
      await shareOrMailto(doc, `Income_Statement_${year}.pdf`, `Income Statement — ${year}`, lines.join("\n"));
    } catch (err: any) {
      if (err?.name !== "AbortError") toast.error("Share failed");
    } finally { setSharing(false); }
  };

  const handleDownload = async () => {
    const doc = await buildPDF();
    if (!doc) return;
    doc.save(`Income_Statement_${year}.pdf`);
    
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-24 h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filterMinistry} onValueChange={setFilterMinistry}>
            <SelectTrigger className="w-44 h-8 text-sm"><SelectValue placeholder="All Ministries" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Ministries</SelectItem>
              <SelectItem value="__none__">No Ministry</SelectItem>
              {sortedOrganizations.map((org) => (
                <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button size="sm" variant="outline" className="h-8 gap-1.5" onClick={() => setPreviewOpen(true)} disabled={isLoading || filteredRecords.length === 0}>
          <Download className="w-3.5 h-3.5" />Preview
        </Button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="stat-card p-4"><p className="text-xs text-muted-foreground mb-1">Total Income</p><p className={cn("text-xl font-bold", positiveTextClass)}>{fmtShort(totalIncome)}</p></div>
        <div className="stat-card p-4"><p className="text-xs text-muted-foreground mb-1">Total Expenses</p><p className={cn("text-xl font-bold", negativeTextClass)}>{fmtShort(totalExpenses)}</p></div>
        <div className="stat-card p-4"><p className="text-xs text-muted-foreground mb-1">Net Income</p><p className={cn("text-xl font-bold", netIncome >= 0 ? positiveTextClass : negativeTextClass)}>{fmtShort(netIncome)}</p></div>
      </div>

      {isLoading ? (
        <div className="stat-card py-10 text-center text-muted-foreground text-sm">Loading…</div>
      ) : filteredRecords.length === 0 ? (
        <div className="stat-card py-10 text-center text-muted-foreground text-sm">No records found for {year} in {selectedMinistryName}.</div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="stat-card p-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-border/40 flex items-center gap-2">
              <ArrowUpRight className={cn("w-4 h-4", positiveTextClass)} />
              <span className="text-sm font-semibold text-foreground">Income</span>
              <Badge variant="outline" className={cn("text-xs ml-auto", positiveTextClass, positiveBorderClass)}>income</Badge>
            </div>
            <table className="w-full text-sm">
              <tbody>{incomeByCategory.map(([cat, amt]) => (
                <tr key={cat} className="border-b border-border/30 last:border-0">
                  <td className="px-4 py-2.5 text-foreground">{cat}</td>
                  <td className="px-4 py-2.5 text-right font-medium text-foreground">{fmt(amt)}</td>
                  <td className="px-4 py-2.5 text-right w-16"><span className="text-xs text-muted-foreground">{totalIncome > 0 ? Math.round((amt / totalIncome) * 100) : 0}%</span></td>
                </tr>
              ))}</tbody>
              <tfoot><tr className={positiveBgClass}><td className="px-4 py-2.5 font-bold text-foreground">Total</td><td className={cn("px-4 py-2.5 text-right font-bold", positiveTextClass)} colSpan={2}>{fmt(totalIncome)}</td></tr></tfoot>
            </table>
          </div>
          <div className="stat-card p-0 overflow-hidden">
            <div className="px-4 py-3 border-b border-border/40 flex items-center gap-2">
              <ArrowDownRight className={cn("w-4 h-4", negativeTextClass)} />
              <span className="text-sm font-semibold text-foreground">Expenses</span>
              <Badge variant="outline" className={cn("text-xs ml-auto", negativeTextClass, negativeBorderClass)}>expense</Badge>
            </div>
            <table className="w-full text-sm">
              <tbody>{expenseByCategory.map(([cat, amt]) => (
                <tr key={cat} className="border-b border-border/30 last:border-0">
                  <td className="px-4 py-2.5 text-foreground">{cat}</td>
                  <td className="px-4 py-2.5 text-right font-medium text-foreground">{fmt(amt)}</td>
                  <td className="px-4 py-2.5 text-right w-16"><span className="text-xs text-muted-foreground">{totalExpenses > 0 ? Math.round((amt / totalExpenses) * 100) : 0}%</span></td>
                </tr>
              ))}</tbody>
              <tfoot><tr className={negativeBgClass}><td className="px-4 py-2.5 font-bold text-foreground">Total</td><td className={cn("px-4 py-2.5 text-right font-bold", negativeTextClass)} colSpan={2}>{fmt(totalExpenses)}</td></tr></tfoot>
            </table>
          </div>
        </div>
      )}

      {/* ── Preview Modal ── */}
      <Dialog open={previewOpen} onOpenChange={(v) => !v && setPreviewOpen(false)}>
        <DialogContent className={REPORT_PREVIEW_DIALOG_CLASS}>
          <DialogHeader><DialogTitle className="font-heading">Income Statement — {year}</DialogTitle></DialogHeader>
          <ChurchReportHeader profile={profile} />
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 py-3 text-sm border-b border-border/40">
            <div><span className="text-muted-foreground text-xs">Report</span><p className="font-medium text-foreground">Income Statement</p></div>
            <div><span className="text-muted-foreground text-xs">Year</span><p className="font-medium text-foreground">{year}</p></div>
            <div><span className="text-muted-foreground text-xs">Ministry</span><p className="font-medium text-foreground">{selectedMinistryName}</p></div>
            <div><span className="text-muted-foreground text-xs">Generated</span><p className="font-medium text-foreground">{new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</p></div>
          </div>
          <div className="space-y-3">
            <div className="border border-border rounded-lg overflow-hidden">
              <div className={cn("px-4 py-2 border-b border-border flex items-center gap-2", positiveBgClass)}>
                <ArrowUpRight className={cn("w-3.5 h-3.5", positiveTextClass)} />
                <span className="text-xs font-semibold uppercase tracking-wide text-foreground">Income</span>
                <Badge variant="outline" className={cn("text-xs ml-auto", positiveTextClass, positiveBorderClass)}>income</Badge>
              </div>
              <table className="w-full text-sm">
                <thead><tr className="bg-secondary/20 border-b border-border/40">
                  <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Category</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Amount</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground uppercase w-14">%</th>
                </tr></thead>
                <tbody>{incomeByCategory.map(([cat, amt]) => (
                  <tr key={cat} className="border-b border-border/30 last:border-0">
                    <td className="px-4 py-2.5 text-foreground">{cat}</td>
                    <td className="px-4 py-2.5 text-right font-medium text-foreground">{fmt(amt)}</td>
                    <td className="px-4 py-2.5 text-right text-xs text-muted-foreground">{totalIncome > 0 ? Math.round((amt / totalIncome) * 100) : 0}%</td>
                  </tr>
                ))}</tbody>
                <tfoot><tr className={cn(positiveBgClass, "border-t border-border")}><td className="px-4 py-2.5 font-bold text-foreground">Total Income</td><td className={cn("px-4 py-2.5 text-right font-bold", positiveTextClass)} colSpan={2}>{fmt(totalIncome)}</td></tr></tfoot>
              </table>
            </div>
            <div className="border border-border rounded-lg overflow-hidden">
              <div className={cn("px-4 py-2 border-b border-border flex items-center gap-2", negativeBgClass)}>
                <ArrowDownRight className={cn("w-3.5 h-3.5", negativeTextClass)} />
                <span className="text-xs font-semibold uppercase tracking-wide text-foreground">Expenses</span>
                <Badge variant="outline" className={cn("text-xs ml-auto", negativeTextClass, negativeBorderClass)}>expense</Badge>
              </div>
              <table className="w-full text-sm">
                <thead><tr className="bg-secondary/20 border-b border-border/40">
                  <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Category</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Amount</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground uppercase w-14">%</th>
                </tr></thead>
                <tbody>{expenseByCategory.map(([cat, amt]) => (
                  <tr key={cat} className="border-b border-border/30 last:border-0">
                    <td className="px-4 py-2.5 text-foreground">{cat}</td>
                    <td className="px-4 py-2.5 text-right font-medium text-foreground">{fmt(amt)}</td>
                    <td className="px-4 py-2.5 text-right text-xs text-muted-foreground">{totalExpenses > 0 ? Math.round((amt / totalExpenses) * 100) : 0}%</td>
                  </tr>
                ))}</tbody>
                <tfoot><tr className={cn(negativeBgClass, "border-t border-border")}><td className="px-4 py-2.5 font-bold text-foreground">Total Expenses</td><td className={cn("px-4 py-2.5 text-right font-bold", negativeTextClass)} colSpan={2}>{fmt(totalExpenses)}</td></tr></tfoot>
              </table>
            </div>
          </div>
          <p className="text-xs text-muted-foreground italic">This report is generated from recorded financial transactions for {year} ({selectedMinistryName}).</p>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button>
            <Button variant="outline" className="gap-1.5" onClick={handleShare} disabled={sharing}>
              {sharing ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}Share
            </Button>
            <Button onClick={handleDownload} disabled={downloading}>
              {downloading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</> : <><Download className="w-4 h-4 mr-2" />PDF</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Budget Summary ────────────────────────────────────────────────────────────
function BudgetSummary({ formatCurrency }: { formatCurrency: (v: number) => string }) {
  const { profile } = useChurch();
  const { getFinanceOrganizationIds, hasFinanceScopedAccess, isChurchAdmin } = usePermissions();
  const financeScoped = !isChurchAdmin && hasFinanceScopedAccess();
  const allowedOrgIds = getFinanceOrganizationIds();
  const [year, setYear] = useState(String(currentYear));
  const [filterMinistry, setFilterMinistry] = useState("all");
  const [previewOpen, setPreviewOpen] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const { data: organizations = [] } = useQuery({
    queryKey: ["organizations", "budget_report_scope", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("organizations")
        .select("id, name")
        .eq("church_id", profile!.id)
        .order("name");
      if (error) throw error;
      return sortGeneralFirstByName((data || []) as { id: string; name: string }[]);
    },
    enabled: !!profile?.id,
  });

  const sortedOrganizations = useMemo(() => {
    const sorted = sortGeneralFirstByName(organizations);
    if (!financeScoped) return sorted;
    return sorted.filter((o) => allowedOrgIds.includes(o.id));
  }, [organizations, financeScoped, allowedOrgIds]);

  const { data: budgets = [], isLoading: loadingBudgets } = useQuery({
    queryKey: ["annual_budgets_report", profile?.id, year],
    queryFn: async () => {
      const { data, error } = await supabase.from("annual_budgets").select("*").eq("church_id", profile!.id).eq("year", Number(year));
      if (error) throw error;
      return data as any[];
    },
    enabled: !!profile?.id,
  });

  const { data: records = [], isLoading: loadingRecords } = useQuery({
    queryKey: ["finance_records_budget_report", profile?.id, year],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("finance_records")
        .select("amount, category, record_type, notes")
        .eq("church_id", profile!.id)
        .gte("date", `${year}-01-01`)
        .lte("date", `${year}-12-31`);
      if (error) throw error;
      return data as { amount: number; category: string; record_type: string; notes: string | null }[];
    },
    enabled: !!profile?.id,
  });

  const scopedBudgets = useMemo(() => {
    if (!financeScoped) return budgets;
    return budgets.filter((b: any) => b.organization_id && allowedOrgIds.includes(b.organization_id));
  }, [budgets, financeScoped, allowedOrgIds]);

  const scopedRecords = useMemo(() => {
    if (!financeScoped) return records;
    return records.filter((r) => {
      const ids = getOrgIdsFromNotes(r.notes);
      return ids.some((id) => allowedOrgIds.includes(id));
    });
  }, [records, financeScoped, allowedOrgIds]);

  const filteredBudgets = useMemo(() => {
    if (filterMinistry === "all") return scopedBudgets;
    if (filterMinistry === "__none__") return scopedBudgets.filter((b: any) => !b.organization_id);
    return scopedBudgets.filter((b: any) => b.organization_id === filterMinistry);
  }, [scopedBudgets, filterMinistry]);

  const filteredRecords = useMemo(() => {
    if (filterMinistry === "all") return scopedRecords;
    if (filterMinistry === "__none__") {
      return scopedRecords.filter((r) => getOrgIdsFromNotes(r.notes).length === 0);
    }
    return scopedRecords.filter((r) => getOrgIdsFromNotes(r.notes).includes(filterMinistry));
  }, [scopedRecords, filterMinistry]);

  const selectedMinistryName = useMemo(() => {
    if (filterMinistry === "all") return "All Ministries";
    if (filterMinistry === "__none__") return "No Ministry";
    return organizations.find((o) => o.id === filterMinistry)?.name || "Unknown Ministry";
  }, [filterMinistry, organizations]);

  const rows = useMemo(() => {
    const actualMap: Record<string, number> = {};
    filteredRecords.forEach((r) => {
      const key = `${r.record_type}::${r.category}`;
      actualMap[key] = (actualMap[key] || 0) + Number(r.amount);
    });
    return filteredBudgets.map((b: any) => {
      const budgeted = MONTHS_SHORT.reduce((sum, m) => sum + (Number(b[m]) || 0), 0);
      const actual = actualMap[`${b.record_type}::${b.category}`] || 0;
      const variance = b.record_type === "income" ? actual - budgeted : budgeted - actual;
      return { ...b, budgeted, actual, variance };
    }).sort((a: any, b: any) => {
      if (a.record_type !== b.record_type) return a.record_type === "income" ? -1 : 1;
      return a.category.localeCompare(b.category);
    });
  }, [filteredBudgets, filteredRecords]);

  const incomeBudgeted = rows.filter((r: any) => r.record_type === "income").reduce((s: number, r: any) => s + r.budgeted, 0);
  const incomeActual = rows.filter((r: any) => r.record_type === "income").reduce((s: number, r: any) => s + r.actual, 0);
  const expenseBudgeted = rows.filter((r: any) => r.record_type === "expense").reduce((s: number, r: any) => s + r.budgeted, 0);
  const expenseActual = rows.filter((r: any) => r.record_type === "expense").reduce((s: number, r: any) => s + r.actual, 0);
  const isLoading = loadingBudgets || loadingRecords;

  const buildBudgetPDF = async () => {
    if (!profile) return null;
    setDownloading(true);
    try {
      const doc = new jsPDF({ unit: "mm", format: "a4" });
      const pageW = doc.internal.pageSize.getWidth();
      const mL = 20, mR = 20;
      const contentW = pageW - mL - mR;
      let y = 20;

      let logoHeight = 0;
      if (profile.logo_url) {
        const logoInfo = await loadImageAsBase64(profile.logo_url);
        if (logoInfo) {
          const maxSize = 18;
          const aspectRatio = logoInfo.width / logoInfo.height;
          let logoWidth = maxSize;
          let logoH = maxSize;
          if (aspectRatio > 1) { logoH = maxSize / aspectRatio; }
          else { logoWidth = maxSize * aspectRatio; }
          try { doc.addImage(logoInfo.data, "PNG", mL, y, logoWidth, logoH); } catch {}
          logoHeight = logoH;
        }
      }
      const headerX = logoHeight > 0 ? mL + 22 : mL;
      doc.setFontSize(16); doc.setFont("helvetica", "bold"); doc.setTextColor(0);
      doc.text(profile.church_name, headerX, y + 7);
      const infoLines = getChurchInfoLines(profile);
      doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.setTextColor(100);
      infoLines.forEach((line, i) => doc.text(line, headerX, y + 12 + i * 4));
      y += Math.max(22, 12 + infoLines.length * 4 + 4);
      doc.setDrawColor(200); doc.setLineWidth(0.5); doc.line(mL, y, pageW - mR, y); y += 8;

      doc.setFontSize(14); doc.setFont("helvetica", "bold"); doc.setTextColor(0);
      doc.text("BUDGET REPORT", mL, y); y += 7;
      doc.setFontSize(9); doc.setFont("helvetica", "normal"); doc.setTextColor(80);
      doc.text(`Year: ${year}`, mL, y); y += 5;
      doc.text(`Ministry: ${selectedMinistryName}`, mL, y); y += 5;
      doc.text(`Generated: ${new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`, mL, y); y += 10;
      doc.setDrawColor(220); doc.line(mL, y, pageW - mR, y); y += 8;

      const colBudgeted = pageW - mR - 65;
      const colActual = pageW - mR - 43;
      const colVariance = pageW - mR - 20;
      const colUsed = pageW - mR;
      doc.setFillColor(240, 240, 240); doc.rect(mL, y - 4, contentW, 7, "F");
      doc.setFont("helvetica", "bold"); doc.setFontSize(8); doc.setTextColor(80);
      doc.text("Category", mL + 2, y);
      doc.text("Budgeted", colBudgeted, y, { align: "right" });
      doc.text("Actual", colActual, y, { align: "right" });
      doc.text("Variance", colVariance, y, { align: "right" });
      doc.text("Used", colUsed, y, { align: "right" });
      y += 8;

      let lastType = "";
      doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.setTextColor(30);
      rows.forEach((r: any) => {
        if (r.record_type !== lastType) {
          doc.setFont("helvetica", "bold"); doc.setFontSize(9);
          doc.text(r.record_type === "income" ? "INCOME" : "EXPENSES", mL, y); y += 6;
          lastType = r.record_type;
          doc.setFont("helvetica", "normal"); doc.setFontSize(8);
        }
        if (y > 270) { doc.addPage(); y = 20; }
        const pct = r.budgeted > 0 ? Math.min(100, Math.round((r.actual / r.budgeted) * 100)) : 0;
        doc.setTextColor(30);
        doc.text(r.category, mL + 4, y);
        doc.text(fmt(r.budgeted), colBudgeted, y, { align: "right" });
        doc.text(fmt(r.actual), colActual, y, { align: "right" });
        if (r.variance >= 0) doc.setTextColor(21, 128, 61); else doc.setTextColor(185, 28, 28);
        doc.text((r.variance >= 0 ? "+" : "") + fmt(r.variance), colVariance, y, { align: "right" });
        doc.setTextColor(80); doc.text(`${pct}%`, colUsed, y, { align: "right" });
        y += 6;
      });

      // ── Total Income row ──
      y += 4;
      doc.setFillColor(235, 245, 235); doc.rect(mL, y - 4, contentW, 7, "F");
      doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(30);
      doc.text("TOTAL INCOME", mL + 2, y);
      doc.text(fmt(incomeBudgeted), colBudgeted, y, { align: "right" });
      doc.setTextColor(21, 128, 61); doc.text(fmt(incomeActual), colActual, y, { align: "right" });
      const incVar = incomeActual - incomeBudgeted;
      if (incVar >= 0) doc.setTextColor(21, 128, 61); else doc.setTextColor(185, 28, 28);
      doc.text((incVar >= 0 ? "+" : "") + fmt(incVar), colVariance, y, { align: "right" });
      doc.setTextColor(80);
      const incPct = incomeBudgeted > 0 ? Math.min(100, Math.round((incomeActual / incomeBudgeted) * 100)) : 0;
      doc.text(`${incPct}%`, colUsed, y, { align: "right" }); y += 8;

      // ── Total Expenses row ──
      doc.setFillColor(245, 235, 235); doc.rect(mL, y - 4, contentW, 7, "F");
      doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.setTextColor(30);
      doc.text("TOTAL EXPENSES", mL + 2, y);
      doc.text(fmt(expenseBudgeted), colBudgeted, y, { align: "right" });
      doc.setTextColor(185, 28, 28); doc.text(fmt(expenseActual), colActual, y, { align: "right" });
      const expVar = expenseBudgeted - expenseActual;
      if (expVar >= 0) doc.setTextColor(21, 128, 61); else doc.setTextColor(185, 28, 28);
      doc.text((expVar >= 0 ? "+" : "") + fmt(expVar), colVariance, y, { align: "right" });
      doc.setTextColor(80);
      const expPct = expenseBudgeted > 0 ? Math.min(100, Math.round((expenseActual / expenseBudgeted) * 100)) : 0;
      doc.text(`${expPct}%`, colUsed, y, { align: "right" }); y += 12;

      doc.setFont("helvetica", "italic"); doc.setFontSize(8); doc.setTextColor(120);
      doc.text(`Budget vs. actual comparison for ${year}. Positive variance = under budget (expenses) or over target (income).`, mL, y, { maxWidth: contentW });
      return doc;
    } finally { setDownloading(false); }
  };

  const [sharingBudget, setSharingBudget] = useState(false);

  const handleBudgetShare = async () => {
    if (!profile) return;
    setSharingBudget(true);
    try {
      const doc = await buildBudgetPDF();
      if (!doc) return;
      const lines = [
        `Budget Report — ${year}`,
        `Ministry: ${selectedMinistryName}`,
        "",
        ...rows.map((r: any) => `  ${r.category} (${r.record_type}): Budgeted ${fmt(r.budgeted)}, Actual ${fmt(r.actual)}, Variance ${r.variance >= 0 ? "+" : ""}${fmt(r.variance)}`),
        "",
        `Total Income — Budgeted: ${fmt(incomeBudgeted)}, Actual: ${fmt(incomeActual)}`,
        `Total Expenses — Budgeted: ${fmt(expenseBudgeted)}, Actual: ${fmt(expenseActual)}`,
      ];
      await shareOrMailto(doc, `Budget_Report_${year}.pdf`, `Budget Report — ${year}`, lines.join("\n"));
    } catch (err: any) {
      if (err?.name !== "AbortError") toast.error("Share failed");
    } finally { setSharingBudget(false); }
  };

  const handleBudgetDownload = async () => {
    const doc = await buildBudgetPDF();
    if (!doc) return;
    doc.save(`Budget_Report_${year}.pdf`);
    
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger className="w-24 h-8 text-sm"><SelectValue /></SelectTrigger>
            <SelectContent>{YEARS.map((y) => <SelectItem key={y} value={y}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={filterMinistry} onValueChange={setFilterMinistry}>
            <SelectTrigger className="w-[190px] h-8 text-sm"><SelectValue placeholder="All Ministries" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Ministries</SelectItem>
              <SelectItem value="__none__">No Ministry</SelectItem>
              {sortedOrganizations.map((org) => (
                <SelectItem key={org.id} value={org.id}>{org.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="h-8 w-8 p-0 sm:w-auto sm:px-3"
          onClick={() => setPreviewOpen(true)}
          disabled={isLoading || rows.length === 0}
          aria-label="Preview and download budget report"
        >
          <Download className="w-3.5 h-3.5 sm:mr-1.5" />
          <span className="hidden sm:inline">Preview &amp; Download</span>
        </Button>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="stat-card p-4"><p className="text-xs text-muted-foreground mb-1">Income Budgeted</p><p className="text-lg font-bold text-foreground">{fmtShort(incomeBudgeted)}</p></div>
        <div className="stat-card p-4"><p className="text-xs text-muted-foreground mb-1">Income Actual</p><p className="text-lg font-bold text-success">{fmtShort(incomeActual)}</p></div>
        <div className="stat-card p-4"><p className="text-xs text-muted-foreground mb-1">Expense Budgeted</p><p className="text-lg font-bold text-foreground">{fmtShort(expenseBudgeted)}</p></div>
        <div className="stat-card p-4"><p className="text-xs text-muted-foreground mb-1">Expense Actual</p><p className="text-lg font-bold text-destructive">{fmtShort(expenseActual)}</p></div>
      </div>

      {isLoading ? (
        <div className="stat-card py-10 text-center text-muted-foreground text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="stat-card py-10 text-center text-muted-foreground text-sm">No budget data for {year}. Add budget lines in the Annual Budget tab.</div>
      ) : (
        <div className="stat-card p-0 overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm">
            <thead>
              <tr className="bg-secondary/30 border-b border-border">
                <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase">Category</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase">Budgeted</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase">Actual</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase">Variance</th>
                <th className="px-4 py-2.5 w-36"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r: any, i: number) => {
                const pct = r.budgeted > 0 ? Math.min(100, Math.round((r.actual / r.budgeted) * 100)) : 0;
                const isIncome = r.record_type === "income";
                const varColor = r.variance >= 0 ? "text-success" : "text-destructive";
                const prevType = i > 0 ? (rows[i - 1] as any).record_type : null;
                return (
                  <>
                    {r.record_type !== prevType && (
                      <tr
                        key={`header-${r.record_type}`}
                        className={cn(
                          "border-y",
                          isIncome
                            ? "bg-emerald-50 border-emerald-100"
                            : "bg-red-50 border-red-100"
                        )}
                      >
                        <td colSpan={5} className="px-4 py-2 text-xs font-bold uppercase text-muted-foreground tracking-wider">
                          {isIncome ? "Income" : "Expenses"}
                        </td>
                      </tr>
                    )}
                    <tr key={r.id} className="border-b border-border/30">
                      <td className="px-4 py-3 text-foreground">{r.category}</td>
                      <td className="px-4 py-3 text-right text-foreground">{fmt(r.budgeted)}</td>
                      <td className="px-4 py-3 text-right text-foreground">{fmt(r.actual)}</td>
                      <td className={`px-4 py-3 text-right font-medium ${varColor}`}>{r.variance >= 0 ? "+" : ""}{fmt(r.variance)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5" title={`${pct}% of budget`}>
                          <Progress value={pct} className="h-1.5 flex-1 cursor-default" />
                          <span className="text-xs text-muted-foreground w-8 text-right">{pct}%</span>
                        </div>
                      </td>
                    </tr>
                  </>
                );
              })}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {/* ── Preview Modal ── */}
      <Dialog open={previewOpen} onOpenChange={(v) => !v && setPreviewOpen(false)}>
        <DialogContent className={REPORT_PREVIEW_DIALOG_CLASS}>
          <DialogHeader><DialogTitle className="font-heading">Budget Report — {year}</DialogTitle></DialogHeader>
          <ChurchReportHeader profile={profile} />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 py-3 text-sm border-b border-border/40">
            <div><span className="text-muted-foreground text-xs">Report</span><p className="font-medium text-foreground">Budget Report</p></div>
            <div><span className="text-muted-foreground text-xs">Year</span><p className="font-medium text-foreground">{year}</p></div>
            <div><span className="text-muted-foreground text-xs">Ministry</span><p className="font-medium text-foreground">{selectedMinistryName}</p></div>
            <div><span className="text-muted-foreground text-xs">Generated</span><p className="font-medium text-foreground">{new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</p></div>
          </div>
          <div className="border border-border rounded-lg overflow-hidden">
            <table className="w-full text-sm">
              <thead><tr className="bg-secondary/30 border-b border-border">
                <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase">Category</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase">Budgeted</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase">Actual</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase">Variance</th>
                <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase w-16">Used</th>
              </tr></thead>
              <tbody>
                {rows.map((r: any, i: number) => {
                  const pct = r.budgeted > 0 ? Math.min(100, Math.round((r.actual / r.budgeted) * 100)) : 0;
                  const isIncome = r.record_type === "income";
                  const varColor = r.variance >= 0 ? "text-success" : "text-destructive";
                  const prevType = i > 0 ? (rows[i - 1] as any).record_type : null;
                  return (
                    <>
                      {r.record_type !== prevType && (
                        <tr
                          key={`ph-${r.record_type}`}
                          className={cn(
                            "border-y",
                            isIncome
                              ? "bg-emerald-50 border-emerald-100"
                              : "bg-red-50 border-red-100"
                          )}
                        >
                          <td colSpan={5} className="px-4 py-2 text-xs font-bold uppercase text-muted-foreground tracking-wider">
                            {isIncome ? "Income" : "Expenses"}
                          </td>
                        </tr>
                      )}
                      <tr key={`p-${r.id}`} className="border-b border-border/30">
                        <td className="px-4 py-2.5 text-foreground">{r.category}</td>
                        <td className="px-4 py-2.5 text-right text-foreground">{fmt(r.budgeted)}</td>
                        <td className="px-4 py-2.5 text-right text-foreground">{fmt(r.actual)}</td>
                        <td className={`px-4 py-2.5 text-right font-medium ${varColor}`}>{r.variance >= 0 ? "+" : ""}{fmt(r.variance)}</td>
                        <td className="px-4 py-2.5 text-right text-xs text-muted-foreground">{pct}%</td>
                      </tr>
                    </>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-border bg-secondary/30">
                  <td className="px-4 py-2.5 font-bold text-foreground text-xs uppercase tracking-wide">Total Income</td>
                  <td className="px-4 py-2.5 text-right font-bold text-foreground">{fmt(incomeBudgeted)}</td>
                  <td className="px-4 py-2.5 text-right font-bold text-success">{fmt(incomeActual)}</td>
                  <td className={`px-4 py-2.5 text-right font-bold ${incomeActual >= incomeBudgeted ? "text-success" : "text-destructive"}`}>
                    {incomeActual >= incomeBudgeted ? "+" : ""}{fmt(incomeActual - incomeBudgeted)}
                  </td>
                  <td />
                </tr>
                <tr className="border-t border-border bg-secondary/20">
                  <td className="px-4 py-2.5 font-bold text-foreground text-xs uppercase tracking-wide">Total Expenses</td>
                  <td className="px-4 py-2.5 text-right font-bold text-foreground">{fmt(expenseBudgeted)}</td>
                  <td className="px-4 py-2.5 text-right font-bold text-destructive">{fmt(expenseActual)}</td>
                  <td className={`px-4 py-2.5 text-right font-bold ${expenseActual <= expenseBudgeted ? "text-success" : "text-destructive"}`}>
                    {expenseActual <= expenseBudgeted ? "+" : ""}{fmt(expenseBudgeted - expenseActual)}
                  </td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
          <p className="text-xs text-muted-foreground italic">Budget vs. actual comparison for {year}. Variance = positive means under budget (good for expenses) or over target (good for income).</p>
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="outline" onClick={() => setPreviewOpen(false)}>Close</Button>
            <Button variant="outline" className="gap-1.5" onClick={handleBudgetShare} disabled={sharingBudget}>
              {sharingBudget ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}Share
            </Button>
            <Button onClick={handleBudgetDownload} disabled={downloading}>
              {downloading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</> : <><Download className="w-4 h-4 mr-2" />PDF</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── Project Reports ───────────────────────────────────────────────────────────
function ProjectReports({ formatCurrency }: { formatCurrency: (v: number) => string }) {
  const { profile } = useChurch();
  const { getFinanceOrganizationIds, hasFinanceScopedAccess, isChurchAdmin } = usePermissions();
  const financeScoped = !isChurchAdmin && hasFinanceScopedAccess();
  const allowedOrgIds = getFinanceOrganizationIds();
  const [expanded, setExpanded] = useState<string | null>(null);
  const [previewProject, setPreviewProject] = useState<any | null>(null);
  const [downloading, setDownloading] = useState(false);

  const { data: allProjects = [], isLoading } = useQuery({
    queryKey: ["project_budgets_report", profile?.id],
    queryFn: async () => {
      const { data, error } = await supabase.from("project_budgets").select("*").eq("church_id", profile!.id).order("created_at", { ascending: false });
      if (error) throw error;
      return data as any[];
    },
    enabled: !!profile?.id,
  });

  const projects = useMemo(() => {
    if (!financeScoped) return allProjects;
    return allProjects.filter((p: any) => p.organization_id && allowedOrgIds.includes(p.organization_id));
  }, [allProjects, financeScoped, allowedOrgIds]);

  const { data: allItems = [] } = useQuery({
    queryKey: ["budget_items_report", profile?.id],
    queryFn: async () => {
      const { data, error } = await (supabase as any).from("project_budget_items").select("*").eq("church_id", profile!.id);
      if (error) throw error;
      return data as any[];
    },
    enabled: !!profile?.id,
  });

  const buildProjectPDF = async (project: any) => {
    if (!profile) return null;
    setDownloading(true);
    try {
    const items = allItems.filter((i: any) => i.project_budget_id === project.id);
    const doc = new jsPDF({ unit: "mm", format: "a4" });
    const pageW = doc.internal.pageSize.getWidth();
    const mL = 20, mR = 20;
    const contentW = pageW - mL - mR;
    let y = 20;

    let logoHeight = 0;
    if (profile.logo_url) {
      const logoInfo = await loadImageAsBase64(profile.logo_url);
      if (logoInfo) {
        const maxSize = 18;
        const aspectRatio = logoInfo.width / logoInfo.height;
        let logoWidth = maxSize;
        let logoH = maxSize;
        if (aspectRatio > 1) { logoH = maxSize / aspectRatio; }
        else { logoWidth = maxSize * aspectRatio; }
        try { doc.addImage(logoInfo.data, "PNG", mL, y, logoWidth, logoH); } catch {}
        logoHeight = logoH;
      }
    }
    const headerX = logoHeight > 0 ? mL + 22 : mL;
    doc.setFontSize(16); doc.setFont("helvetica", "bold"); doc.setTextColor(0);
    doc.text(profile.church_name, headerX, y + 7);
    const infoLines = getChurchInfoLines(profile);
    doc.setFontSize(8); doc.setFont("helvetica", "normal"); doc.setTextColor(100);
    infoLines.forEach((line, idx) => doc.text(line, headerX, y + 12 + idx * 4));
    y += Math.max(22, 12 + infoLines.length * 4 + 4);
    doc.setDrawColor(200); doc.setLineWidth(0.5); doc.line(mL, y, pageW - mR, y); y += 8;

    doc.setFontSize(14); doc.setFont("helvetica", "bold"); doc.setTextColor(0);
    doc.text("PROJECT REPORT", mL, y);
    y += 8;
    doc.setFontSize(13);
    doc.text(project.name, mL, y);
    y += 6;
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    doc.setTextColor(100);
    if (project.description) { doc.text(project.description, mL, y); y += 5; }
    doc.text(`Total Budget: ${fmt(project.total_budget)}`, mL, y); y += 5;
    doc.text(`Status: ${project.status}`, mL, y); y += 5;
    if (project.start_date) { doc.text(`Start: ${fmtDate(project.start_date)}`, mL, y); y += 5; }
    if (project.end_date) { doc.text(`End: ${fmtDate(project.end_date)}`, mL, y); y += 5; }
    doc.text(`Generated: ${new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}`, mL, y); y += 10;

    doc.setDrawColor(200);
    doc.line(mL, y, pageW - mR, y);
    y += 8;

    const colBudgeted = pageW - mR - 60;
    const colActual = pageW - mR - 40;
    const colVariance = pageW - mR - 18;
    const colUsed = pageW - mR;

    doc.setFillColor(240, 240, 240);
    doc.rect(mL, y - 4, contentW, 7, "F");
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8);
    doc.setTextColor(80);
    doc.text("Category", mL + 2, y);
    doc.text("Budgeted", colBudgeted, y, { align: "right" });
    doc.text("Actual", colActual, y, { align: "right" });
    doc.text("Variance", colVariance, y, { align: "right" });
    doc.text("Used", colUsed, y, { align: "right" });
    y += 8;

    doc.setFont("helvetica", "normal");
    doc.setTextColor(30);
    let totalBudgeted = 0, totalActual = 0;
    items.forEach((item: any) => {
      const bAmt = Number(item.budgeted_amount);
      const aAmt = Number(item.actual_amount);
      const v = bAmt - aAmt;
      const pct = bAmt > 0 ? Math.min(100, Math.round((aAmt / bAmt) * 100)) : 0;
      doc.setTextColor(30);
      doc.text(item.category, mL + 4, y);
      doc.text(fmt(bAmt), colBudgeted, y, { align: "right" });
      doc.text(fmt(aAmt), colActual, y, { align: "right" });
      if (v >= 0) doc.setTextColor(21, 128, 61); else doc.setTextColor(185, 28, 28);
      doc.text((v >= 0 ? "+" : "") + fmt(v), colVariance, y, { align: "right" });
      doc.setTextColor(80); doc.text(`${pct}%`, colUsed, y, { align: "right" });
      totalBudgeted += bAmt;
      totalActual += aAmt;
      y += 6;
    });

    y += 3;
    doc.setFillColor(240, 240, 240); doc.rect(mL, y - 4, contentW, 7, "F");
    doc.setFont("helvetica", "bold"); doc.setTextColor(30);
    doc.text("TOTAL", mL + 2, y);
    doc.text(fmt(totalBudgeted), colBudgeted, y, { align: "right" });
    doc.text(fmt(totalActual), colActual, y, { align: "right" });
    const totalVar = totalBudgeted - totalActual;
    const totalPct = totalBudgeted > 0 ? Math.min(100, Math.round((totalActual / totalBudgeted) * 100)) : 0;
    if (totalVar >= 0) doc.setTextColor(21, 128, 61); else doc.setTextColor(185, 28, 28);
    doc.text((totalVar >= 0 ? "+" : "") + fmt(totalVar), colVariance, y, { align: "right" });
    doc.setTextColor(80); doc.text(`${totalPct}%`, colUsed, y, { align: "right" });

    return doc;
    } finally { setDownloading(false); }
  };

  const [sharingProject, setSharingProject] = useState(false);

  const handleProjectShare = async (project: any) => {
    if (!profile) return;
    setSharingProject(true);
    try {
      const doc = await buildProjectPDF(project);
      if (!doc) return;
      const items = allItems.filter((i: any) => i.project_budget_id === project.id);
      const lines = [
        `Project Report — ${project.name}`,
        "",
        `Total Budget: ${fmt(project.total_budget)}`,
        `Status: ${project.status}`,
        "",
        ...items.map((i: any) => `  ${i.category}: Budgeted ${fmt(i.budgeted_amount)}, Actual ${fmt(i.actual_amount)}`),
      ];
      const fileName = `Project_${project.name.replace(/\s+/g, "_")}.pdf`;
      await shareOrMailto(doc, fileName, `Project Report — ${project.name}`, lines.join("\n"));
    } catch (err: any) {
      if (err?.name !== "AbortError") toast.error("Share failed");
    } finally { setSharingProject(false); }
  };

  const handleProjectDownload = async (project: any) => {
    const doc = await buildProjectPDF(project);
    if (!doc) return;
    doc.save(`Project_${project.name.replace(/\s+/g, "_")}.pdf`);
    
  };

  const statusColor: Record<string, string> = {
    active: "bg-success/10 text-success border-success/20",
    completed: "bg-primary/10 text-primary border-primary/20",
    paused: "bg-warning/10 text-warning border-warning/20",
    cancelled: "bg-destructive/10 text-destructive border-destructive/20",
  };

  return (
    <div className="space-y-3">
      {isLoading ? (
        <div className="stat-card py-10 text-center text-muted-foreground text-sm">Loading…</div>
      ) : projects.length === 0 ? (
        <div className="stat-card py-10 text-center text-muted-foreground text-sm">No projects found. Create projects in the Projects tab.</div>
      ) : (
        projects.map((project: any) => {
          const items = allItems.filter((i: any) => i.project_budget_id === project.id);
          const totalActual = items.reduce((s: number, i: any) => s + Number(i.actual_amount), 0);
          const pct = project.total_budget > 0 ? Math.min(100, Math.round((totalActual / project.total_budget) * 100)) : 0;
          const isOpen = expanded === project.id;

          return (
            <div key={project.id} className="stat-card p-0 overflow-hidden">
              <div
                className="px-4 py-3 flex items-center gap-3 cursor-pointer hover:bg-muted/30 transition-colors"
                onClick={() => setExpanded(isOpen ? null : project.id)}
              >
                <ChevronRight className={`w-4 h-4 text-muted-foreground transition-transform ${isOpen ? "rotate-90" : ""}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-semibold text-foreground text-sm">{project.name}</span>
                    <Badge variant="outline" className={`text-xs ${statusColor[project.status] || ""}`}>{project.status}</Badge>
                  </div>
                  <div className="flex items-center gap-4 mt-1.5">
                    <Progress value={pct} className="h-1.5 w-32" />
                    <span className="text-xs text-muted-foreground">{fmt(totalActual)} / {fmt(project.total_budget)} ({pct}%)</span>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 gap-1 text-xs shrink-0"
                  onClick={(e) => { e.stopPropagation(); setPreviewProject(project); }}
                >
                  <Download className="w-3 h-3" />Preview
                </Button>
              </div>

              {isOpen && (
                <div className="border-t border-border/40">
                  {items.length === 0 ? (
                    <p className="px-4 py-4 text-sm text-muted-foreground">No budget line items for this project.</p>
                  ) : (
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-secondary/20 border-b border-border/30">
                          <th className="text-left px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Category</th>
                          <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Budgeted</th>
                          <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Actual</th>
                          <th className="text-right px-4 py-2 text-xs font-medium text-muted-foreground uppercase">Variance</th>
                          <th className="px-4 py-2 w-32"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item: any) => {
                          const variance = Number(item.budgeted_amount) - Number(item.actual_amount);
                          const itemPct = item.budgeted_amount > 0 ? Math.min(100, Math.round((item.actual_amount / item.budgeted_amount) * 100)) : 0;
                          return (
                            <tr key={item.id} className="border-b border-border/20 last:border-0">
                              <td className="px-4 py-2.5 text-foreground">{item.category}</td>
                              <td className="px-4 py-2.5 text-right text-foreground">{fmt(item.budgeted_amount)}</td>
                              <td className="px-4 py-2.5 text-right text-foreground">{fmt(item.actual_amount)}</td>
                              <td className={`px-4 py-2.5 text-right font-medium ${variance >= 0 ? "text-success" : "text-destructive"}`}>
                                {variance >= 0 ? "+" : ""}{fmt(variance)}
                              </td>
                              <td className="px-4 py-2.5">
                                <div className="flex items-center gap-1.5">
                                  <Progress value={itemPct} className="h-1.5 flex-1" />
                                  <span className="text-xs text-muted-foreground w-7 text-right">{itemPct}%</span>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="bg-secondary/20">
                          <td className="px-4 py-2.5 font-bold text-foreground">Total</td>
                          <td className="px-4 py-2.5 text-right font-bold text-foreground">{fmt(items.reduce((s: number, i: any) => s + Number(i.budgeted_amount), 0))}</td>
                          <td className="px-4 py-2.5 text-right font-bold text-foreground">{fmt(totalActual)}</td>
                          <td colSpan={2}></td>
                        </tr>
                      </tfoot>
                    </table>
                  )}
                </div>
              )}
            </div>
          );
        })
      )}
      {/* ── Preview Modal ── */}
      {(() => {
        const previewItems = previewProject ? allItems.filter((i: any) => i.project_budget_id === previewProject.id) : [];
        const previewTotalBudgeted = previewItems.reduce((s: number, i: any) => s + Number(i.budgeted_amount), 0);
        const previewTotalActual = previewItems.reduce((s: number, i: any) => s + Number(i.actual_amount), 0);
        return (
          <Dialog open={!!previewProject} onOpenChange={(v) => !v && setPreviewProject(null)}>
            <DialogContent className={REPORT_PREVIEW_DIALOG_CLASS}>
              <DialogHeader><DialogTitle className="font-heading">Project Report — {previewProject?.name}</DialogTitle></DialogHeader>
              <ChurchReportHeader profile={profile} />
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 py-3 text-sm border-b border-border/40">
                <div><span className="text-muted-foreground text-xs">Project</span><p className="font-medium text-foreground">{previewProject?.name}</p></div>
                <div>
                  <span className="text-muted-foreground text-xs">Status</span>
                  <Badge variant="outline" className={`block w-fit mt-0.5 text-xs ${statusColor[previewProject?.status] || ""}`}>{previewProject?.status}</Badge>
                </div>
                <div><span className="text-muted-foreground text-xs">Total Budget</span><p className="font-medium text-foreground">{fmt(previewProject?.total_budget || 0)}</p></div>
                {previewProject?.start_date && <div><span className="text-muted-foreground text-xs">Start Date</span><p className="font-medium text-foreground">{fmtDate(previewProject.start_date)}</p></div>}
                {previewProject?.end_date && <div><span className="text-muted-foreground text-xs">End Date</span><p className="font-medium text-foreground">{fmtDate(previewProject.end_date)}</p></div>}
                <div><span className="text-muted-foreground text-xs">Generated</span><p className="font-medium text-foreground">{new Date().toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}</p></div>
              </div>
              {previewProject?.description && <p className="text-sm text-muted-foreground">{previewProject.description}</p>}
              <div className="border border-border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="bg-secondary/30 border-b border-border">
                    <th className="text-left px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase">Category</th>
                    <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase">Budgeted</th>
                    <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase">Actual</th>
                    <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase">Variance</th>
                    <th className="text-right px-4 py-2.5 text-xs font-medium text-muted-foreground uppercase w-16">Used</th>
                  </tr></thead>
                  <tbody>
                    {previewItems.map((item: any) => {
                      const variance = Number(item.budgeted_amount) - Number(item.actual_amount);
                      const itemPct = item.budgeted_amount > 0 ? Math.min(100, Math.round((item.actual_amount / item.budgeted_amount) * 100)) : 0;
                      return (
                        <tr key={item.id} className="border-b border-border/20 last:border-0">
                          <td className="px-4 py-2.5 text-foreground">{item.category}</td>
                          <td className="px-4 py-2.5 text-right text-foreground">{fmt(item.budgeted_amount)}</td>
                          <td className="px-4 py-2.5 text-right text-foreground">{fmt(item.actual_amount)}</td>
                          <td className={`px-4 py-2.5 text-right font-medium ${variance >= 0 ? "text-success" : "text-destructive"}`}>
                            {variance >= 0 ? "+" : ""}{fmt(variance)}
                          </td>
                          <td className="px-4 py-2.5 text-right text-xs text-muted-foreground">{itemPct}%</td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="border-t-2 border-border bg-secondary/30">
                      <td className="px-4 py-2.5 font-bold text-foreground">Total</td>
                      <td className="px-4 py-2.5 text-right font-bold text-foreground">{fmt(previewTotalBudgeted)}</td>
                      <td className="px-4 py-2.5 text-right font-bold text-foreground">{fmt(previewTotalActual)}</td>
                      <td className={`px-4 py-2.5 text-right font-bold ${previewTotalActual <= previewTotalBudgeted ? "text-success" : "text-destructive"}`}>
                        {previewTotalActual <= previewTotalBudgeted ? "+" : ""}{fmt(previewTotalBudgeted - previewTotalActual)}
                      </td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <Button variant="outline" onClick={() => setPreviewProject(null)}>Close</Button>
                <Button variant="outline" className="gap-1.5" onClick={() => previewProject && handleProjectShare(previewProject)} disabled={sharingProject}>
                  {sharingProject ? <Loader2 className="w-4 h-4 animate-spin" /> : <Share2 className="w-4 h-4" />}Share
                </Button>
                <Button onClick={async () => { if (previewProject) { await handleProjectDownload(previewProject); setPreviewProject(null); } }} disabled={downloading}>
                  {downloading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Generating...</> : <><Download className="w-4 h-4 mr-2" />PDF</>}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        );
      })()}    </div>
  );
}

// ── Main ReportsHub ───────────────────────────────────────────────────────────
interface Props {
  formatCurrency: (v: number) => string;
}

function ReportsHub({ formatCurrency }: Props) {
  const [activeReport, setActiveReport] = useState<ReportType | null>(null);

  if (activeReport) {
    const card = REPORT_CARDS.find((c) => c.id === activeReport)!;
    const Icon = card.icon;
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" className="h-8 gap-1.5 text-xs" onClick={() => setActiveReport(null)}>
            ← Back
          </Button>
          <div className="flex items-center gap-2">
            <Icon className="w-4 h-4 text-primary" />
            <span className="font-semibold text-foreground">{card.label}</span>
          </div>
        </div>

        {activeReport === "contributions" && <ContributionStatements />}
        {activeReport === "income_statement" && <IncomeStatement formatCurrency={formatCurrency} />}
        {activeReport === "budget_summary" && <BudgetSummary formatCurrency={formatCurrency} />}
        {activeReport === "project_reports" && <ProjectReports formatCurrency={formatCurrency} />}
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-base font-semibold text-foreground">Reports</h2>
        <p className="text-sm text-muted-foreground mt-0.5">Generate and download financial reports for your church.</p>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {REPORT_CARDS.map((card) => {
          const Icon = card.icon;
          return (
            <button
              key={card.id}
              onClick={() => setActiveReport(card.id)}
              className="stat-card p-5 text-left hover:border-primary/40 hover:bg-primary/5 transition-all group"
            >
              <div className="flex items-start justify-between gap-3">
                <div className="flex items-start gap-3">
                  <div className="p-2 rounded-lg bg-primary/10 group-hover:bg-primary/15 transition-colors">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{card.label}</p>
                    <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">{card.description}</p>
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground mt-0.5 shrink-0 group-hover:text-primary transition-colors" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default ReportsHub;
